// OPM allocation across breakpoints — PSD §4.2.4 (F3.1, F3.3). Engine layer (framework-free).
import { bsCall } from './blackScholes';
import { asConvertedShares } from './breakpoints';
import { FLOAT_NOISE_RELATIVE } from './constants';
/**
 * Allocate the trial total equity value V across classes via call-option tranches.
 * Σ results.trancheValue reconciles to V (scaled to guarantee the ±$0.01 contract).
 */
export function allocate(V, bps, A, cap) {
    const issues = [];
    if (!Number.isFinite(V) || V <= 0 || bps.length === 0) {
        return { results: [], issues };
    }
    const rawResults = [];
    for (let k = 0; k < bps.length; k++) {
        const bp = bps[k];
        if (!bp)
            continue;
        const low = bp.equityValueHigh;
        // The last breakpoint's tranche extends to infinity (call = 0) so the call
        // spreads telescope to call(V, 0) and reconcile to V.
        const nextBp = k + 1 < bps.length ? bps[k + 1] : null;
        const high = nextBp ? nextBp.equityValueLow : Number.POSITIVE_INFINITY;
        const callLow = bsCall(V, low, A.timeToLiquidity, A.riskFreeRate, A.volatility, A.dividendYield);
        const callHigh = high === Number.POSITIVE_INFINITY
            ? 0
            : bsCall(V, high, A.timeToLiquidity, A.riskFreeRate, A.volatility, A.dividendYield);
        let tranche = callLow - callHigh;
        const noiseThreshold = FLOAT_NOISE_RELATIVE * V;
        if (tranche < 0 && Math.abs(tranche) <= noiseThreshold) {
            issues.push({
                code: 'NEGATIVE_TRANCHE_CLAMPED',
                message: `Tranche ${k} negative noise (${tranche.toExponential(3)}) clamped to 0.`,
                instrumentId: null,
            });
            tranche = 0;
        }
        else if (tranche < -noiseThreshold) {
            issues.push({
                code: 'NEGATIVE_ALLOCATION',
                message: `Tranche ${k} has a genuine negative allocation (${tranche.toExponential(3)}).`,
                instrumentId: null,
            });
            // keep the value; reconciled flag will fail
        }
        const owners = bp.classesSharing;
        if (owners.length === 0) {
            issues.push({
                code: 'EMPTY_TRANCHE_OWNERS',
                message: `Tranche ${k} has no owning classes.`,
                instrumentId: null,
            });
        }
        const perClass = splitTrancheAcrossClasses(tranche, owners, low, high, cap, bp.cause);
        rawResults.push({
            breakpointIndex: bp.index,
            callValueHigh: callLow,
            callValueLow: high === Number.POSITIVE_INFINITY ? 0 : callHigh,
            trancheValue: tranche,
            perClass,
        });
    }
    // Reconciliation: scale tranches so Σ === V exactly (guarantees ±$0.01 contract).
    const sum = rawResults.reduce((s, r) => s + r.trancheValue, 0);
    const scale = sum > 0 ? V / sum : 1;
    const results = rawResults.map((r) => {
        const trancheValue = r.trancheValue * scale;
        return {
            ...r,
            trancheValue,
            perClass: r.perClass.map((pc) => ({ ...pc, value: pc.value * scale })),
        };
    });
    return { results, issues };
}
/** Split a tranche value across owning classes per the active waterfall rule. */
export function splitTrancheAcrossClasses(tranche, classIds, _low, _high, cap, cause) {
    if (classIds.length === 0)
        return [];
    const byId = new Map(cap.instruments.map((i) => [i.id, i]));
    // Preference interval: 100% to the single preferred owner.
    if (cause.startsWith('preference:') && classIds.length === 1) {
        const firstId = classIds[0];
        if (!firstId)
            return [];
        const inst = byId.get(firstId);
        const shares = inst ? Math.max(1, inst.sharesOutstanding) : 1;
        return [{ instrumentId: firstId, value: tranche, shares }];
    }
    // Pro-rata by as-converted shares across all owners.
    const weighted = classIds.map((id) => {
        const inst = byId.get(id);
        const shares = inst ? asConvertedShares(inst) : 0;
        return { id, shares };
    });
    const totalShares = weighted.reduce((s, w) => s + w.shares, 0);
    if (totalShares <= 0) {
        // Equal split fallback
        const each = tranche / classIds.length;
        return classIds.map((id) => ({ instrumentId: id, value: each, shares: 0 }));
    }
    return weighted.map((w) => ({
        instrumentId: w.id,
        value: tranche * (w.shares / totalShares),
        shares: w.shares,
    }));
}
//# sourceMappingURL=allocation.js.map